import React, { useEffect, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import type { SourceChannel } from '@repo/types'
import { Button, Modal } from '@repo/ui'

import { SelectField } from 'components/input/fields/SelectField'
import { NO_SOURCE_CHANNEL, NOT_RECORDED_LABEL, SOURCE_CHANNEL_PICKER_OPTIONS } from 'constants/sourceChannels'

/**
 * VRG-675 — set (or clear) where a person or a company came from: one of the 19 Traction
 * channels, #13 being Outreach. Used by the People row kebab and the internal Company page.
 * Writing "Not recorded" deletes the record, so it is the honest default, never "Direct".
 *
 * Save is live only when the choice differs from what is stored (§54); Cancel never disables.
 */
export const SourceChannelModal = ({
  open,
  name,
  subject,
  initial,
  loading,
  onClose,
  onSave,
}: {
  open: boolean
  /** Whose channel this is, shown in the copy. */
  name: string
  subject: 'person' | 'company'
  initial: SourceChannel | null
  loading: boolean
  onClose: () => void
  onSave: (channel: SourceChannel | null) => void
}) => {
  const [value, setValue] = useState<string>(initial ?? NO_SOURCE_CHANNEL)
  useEffect(() => {
    if (open) setValue(initial ?? NO_SOURCE_CHANNEL)
  }, [open, initial])
  const dirty = value !== (initial ?? NO_SOURCE_CHANNEL)

  return (
    <Modal
      title="Source channel"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!dirty}
            onClick={() => onSave(value === NO_SOURCE_CHANNEL ? null : (value as SourceChannel))}
          >
            Save
          </Button>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Which channel brought <strong>{name}</strong> to Vera. Staff only, and it feeds GTM → Sales.
          {subject === 'company'
            ? ' A company with no channel of its own counts under the channel of the person who signed it up.'
            : ' If this person goes on to create a company, the company counts under this channel unless it has its own.'}
        </Typography>
        <SelectField
          label="Channel"
          options={SOURCE_CHANNEL_PICKER_OPTIONS}
          placeholder={NOT_RECORDED_LABEL}
          value={value}
          search
          onChange={(v) => setValue(String(v))}
        />
      </Stack>
    </Modal>
  )
}
